import { isNil } from 'lodash';
import { useContext, useEffect, useRef } from 'react';
import { View as RNView, StyleSheet } from 'react-native';
import { useToast } from '@shared/components/Toast';
import { ScrollView, View } from '@shared/components/View';
import { LoadingIndicator } from '@shared/components/loading';
import { CardChannelContext } from '@shared/scenes/cards/context';
import { FormErrors } from '@shared/scenes/entity/components/FormErrors';
import { useFormScrollContext } from '@shared/scenes/entity/context';
import { CardEntityError } from '@shared/scenes/entity/create/hooks/useFormErrors';
import { FieldRecord, UpdateField } from '@shared/scenes/entity/types';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { useNavigator } from '@shared/util/navigation';
import { CardFormTypeContext } from '../../cardFormTypeContext';
import { EntityForm } from '../../components/EntityForm';
import { SubformNavigationContext } from '../../components/fields/Subform/SubformEditField';
import classNames from './CardEntityCreateForm.module.css';

interface Props {
  workflowTemplateId?: string | null;
  viewTemplateId?: string | null;
  fields: FieldRecord | null;
  updateField: UpdateField;
  components: API.AnyComponent[];
  errors: CardEntityError[];
  saveAttempted?: boolean;
  isAuthenticated?: boolean;
  layout?: API.ViewTemplateLayoutFragment;
}

export function CardEntityCreateForm({
  components,
  fields,
  updateField,
  errors,
  workflowTemplateId,
  viewTemplateId,
  saveAttempted,
  isSaveSuccessful,
  savedEntity,
  isAuthenticated,
  layout,
  onSaveSuccess,
}: Props & {
  isSaveSuccessful?: boolean;
  savedEntity?: API.WorkflowEntity;
  /**
   * Where to go once the card exists. Defaults to going back, which is right for a form the user
   * navigated to and nothing else. A form that opens child routes of its own — the create form and
   * its Capture modal — passes its own destination instead, because the entry behind it is one of
   * those children and going back reopens it over a form that has just been submitted.
   */
  onSaveSuccess?: () => void;
}) {
  const { goBack, navigate } = useNavigator();
  const toast = useToast();
  const channel = useContext(CardChannelContext);
  const isNavigating = useRef(false);

  useEffect(() => {
    if (isSaveSuccessful && !isNil(savedEntity) && !isNavigating.current) {
      isNavigating.current = true;
      if (isNil(onSaveSuccess)) {
        goBack();
      } else {
        onSaveSuccess();
      }
      toast({
        message: i18n('card.create.success'),
        actions: [
          {
            label: 'view',
            onPress: () => {
              if (!isNil(channel)) {
                navigate('CardUpdate', {
                  cardId: savedEntity.id,
                  channelSlug: channel.slug,
                });
              }
            },
          },
        ],
      });
    }
  }, [isSaveSuccessful, goBack, navigate, toast, channel, savedEntity, onSaveSuccess]);

  return (
    <CardEntityCreateFormContent
      components={components}
      fields={fields}
      updateField={updateField}
      errors={errors}
      workflowTemplateId={workflowTemplateId}
      viewTemplateId={viewTemplateId}
      saveAttempted={saveAttempted}
      isAuthenticated={isAuthenticated}
      layout={layout}
    />
  );
}

function CardEntityCreateFormContent({
  workflowTemplateId,
  viewTemplateId,
  fields,
  updateField,
  components,
  errors,
  saveAttempted,
  isAuthenticated = true,
  layout,
}: Props) {
  const scrollView = useFormScrollContext();

  if (isNil(fields) || isNil(workflowTemplateId) || isNil(viewTemplateId)) {
    return <LoadingIndicator fill />;
  }

  return (
    <CardFormTypeContext.Provider value="CREATE">
      <SubformNavigationContext
        fields={fields}
        updateField={updateField}
        saveAttempted={saveAttempted}
        apiErrors={errors}
        isAuthenticated={isAuthenticated}
      >
        <RNView style={styles.formPane}>
          <div style={{ position: 'sticky', top: 0, zIndex: 1000 }}>
            <FormErrors errors={errors} />
          </div>
          <ScrollView ref={scrollView} flex fullWidth>
            <View className={classNames.scrollContent}>
              <EntityForm
                fields={fields}
                updateField={updateField}
                components={components}
                errors={errors}
                workflowTemplateId={workflowTemplateId}
                viewTemplateId={viewTemplateId}
                isAuthenticated={isAuthenticated}
                layout={layout}
              />
            </View>
          </ScrollView>
        </RNView>
      </SubformNavigationContext>
    </CardFormTypeContext.Provider>
  );
}

const styles = StyleSheet.create({
  formPane: {
    flex: 1,
  },
});
